type StorageType = 'localStorage' | 'sessionStorage' | 'memory';

export class Storage {
    private type: StorageType;
    private prefix: string;
    private memoryStorage: Map<string, unknown> = new Map();

    constructor(type: StorageType, prefix: string) {
        this.type = this.isStorageAvailable(type) ? type : 'memory';
        this.prefix = prefix;

        if (this.type === 'memory' && type !== 'memory') {
            console.warn(`Honch: ${type} not available, falling back to memory storage`);
        }
    }

    get<T>(key: string): T | null {
        const fullKey = this.prefix + key;

        try {
            if (this.type === 'memory') {
                return (this.memoryStorage.get(fullKey) as T) ?? null;
            }

            const storage = this.type === 'localStorage' ? localStorage : sessionStorage;
            const value = storage.getItem(fullKey);

            if (value === null) return null;

            return JSON.parse(value) as T;
        } catch (_e) {
            return null;
        }
    }

    set(key: string, value: unknown): void {
        const fullKey = this.prefix + key;

        try {
            if (this.type === 'memory') {
                this.memoryStorage.set(fullKey, value);
                return;
            }

            const storage = this.type === 'localStorage' ? localStorage : sessionStorage;
            storage.setItem(fullKey, JSON.stringify(value));
        } catch (_e) {
            // Storage might be full
            console.warn('Honch: Failed to persist to storage', _e);
        }
    }

    remove(key: string): void {
        const fullKey = this.prefix + key;

        try {
            if (this.type === 'memory') {
                this.memoryStorage.delete(fullKey);
                return;
            }

            const storage = this.type === 'localStorage' ? localStorage : sessionStorage;
            storage.removeItem(fullKey);
        } catch (_e) {
            // Ignore
        }
    }

    clear(): void {
        try {
            if (this.type === 'memory') {
                this.memoryStorage.clear();
                return;
            }

            const storage = this.type === 'localStorage' ? localStorage : sessionStorage;
            const keysToRemove: string[] = [];

            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && key.startsWith(this.prefix)) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach((key) => storage.removeItem(key));
        } catch (_e) {
            // Ignore
        }
    }

    private isStorageAvailable(type: StorageType): boolean {
        if (type === 'memory') return true;
        if (typeof window === 'undefined') return false;

        try {
            const storage = type === 'localStorage' ? localStorage : sessionStorage;
            const testKey = '__honch_test__';
            storage.setItem(testKey, 'test');
            storage.removeItem(testKey);
            return true;
        } catch (_e) {
            return false;
        }
    }
}
